import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, FolderPlus, X, Check, AlertCircle, Search, ChevronLeft, ChevronRight, Users as UsersIcon, UserCog } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
// Role values sent to the API; labels come from common:roles.<role>
const ROLE_OPTIONS = ['super_admin', 'admin', 'accountant', 'manager', 'employee', 'pending'];

function Users() {
  const { user: currentUser, checkAuth } = useAuth();
  const { t } = useTranslation('users');
  const isReadOnly = currentUser?.role === 'accountant';
  const canImpersonate = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProjects, setUserProjects] = useState([]);
  const [formData, setFormData] = useState({ email: '', name: '', role: 'employee', picture: '', can_view_finance: false, can_view_analytics: false });

  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filters
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('per_page', perPage);
      if (filterRole && filterRole !== 'all') params.append('role', filterRole);
      if (filterStatus === 'active') params.append('is_active', 'true');
      if (filterStatus === 'inactive') params.append('is_active', 'false');
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API_URL}/api/users?${params.toString()}`, { withCredentials: true });
      setUsers(response.data.items || []);
      setTotalItems(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(t('toasts.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filterRole, filterStatus, searchQuery, t]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [filterRole, filterStatus, searchQuery, perPage]);

  const fetchUserProjects = useCallback(async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}/projects`, { withCredentials: true });
      setUserProjects(response.data);
    } catch (error) {
      console.error('Error fetching user projects:', error);
      setUserProjects([]);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const clearFilters = () => {
    setFilterRole('all');
    setFilterStatus('all');
    setSearchQuery('');
    setSearchInput('');
  };

  const hasActiveFilters = filterRole !== 'all' || filterStatus !== 'all' || searchQuery;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.patch(`${API_URL}/api/users/${editingUser.user_id}`, formData, { withCredentials: true });
        toast.success(t('toasts.updated'));
      } else {
        await axios.post(`${API_URL}/api/users`, formData, { withCredentials: true });
        toast.success(t('toasts.created'));
      }
      setOpen(false);
      setEditingUser(null);
      setFormData({ email: '', name: '', role: 'employee', picture: '', can_view_finance: false });
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.response?.data?.detail || t('toasts.saveFailed'));
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('toasts.deleteConfirm'))) return;
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success(t('toasts.deleted'));
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      const errorMessage = error.response?.data?.detail || t('toasts.deleteFailed');
      toast.error(errorMessage, { duration: 6000 });
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      await axios.patch(`${API_URL}/api/users/${user.user_id}`, { is_active: !user.is_active }, { withCredentials: true });
      toast.success(!user.is_active ? t('toasts.activated') : t('toasts.deactivated'));
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error(t('toasts.statusFailed'));
    }
  };

  const assignProject = async (projectId) => {
    if (!selectedUser) return;
    try {
      await axios.post(`${API_URL}/api/users/${selectedUser.user_id}/assign-project/${projectId}`, {}, { withCredentials: true });
      toast.success(t('toasts.projectAssigned'));
      fetchUserProjects(selectedUser.user_id);
    } catch (error) {
      console.error('Error assigning project:', error);
      toast.error(error.response?.data?.detail || t('toasts.assignFailed'));
    }
  };

  const unassignProject = async (projectId) => {
    if (!selectedUser) return;
    try {
      await axios.delete(`${API_URL}/api/users/${selectedUser.user_id}/unassign-project/${projectId}`, { withCredentials: true });
      toast.success(t('toasts.projectUnassigned'));
      fetchUserProjects(selectedUser.user_id);
    } catch (error) {
      console.error('Error unassigning project:', error);
      toast.error(error.response?.data?.detail || t('toasts.unassignFailed'));
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({ email: user.email, name: user.name, role: user.role, picture: user.picture || '', can_view_finance: user.can_view_finance || false, can_view_analytics: user.can_view_analytics || false });
    setOpen(true);
  };

  const openAddDialog = () => {
    setEditingUser(null);
    setFormData({ email: '', name: '', role: 'employee', picture: '', can_view_finance: false, can_view_analytics: false });
    setOpen(true);
  };

  const openProjectDialog = async (user) => {
    setSelectedUser(user);
    await fetchUserProjects(user.user_id);
    setProjectDialogOpen(true);
  };

  const quickApproveUser = async (user) => {
    try {
      if (user.role === 'pending') {
        // Least privilege: an approved sign-up starts as an employee (the admin can raise it later)
        await axios.patch(`${API_URL}/api/users/${user.user_id}`, { role: 'employee' }, { withCredentials: true });
      }
      if (!user.is_active) {
        await axios.patch(`${API_URL}/api/users/${user.user_id}`, { is_active: true }, { withCredentials: true });
      }
      toast.success(t('toasts.approved'));
      fetchUsers();
      openProjectDialog({ ...user, role: user.role === 'pending' ? 'employee' : user.role, is_active: true });
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error(t('toasts.approveFailed'));
    }
  };

  const handleImpersonate = async (targetUser) => {
    try {
      await axios.post(`${API_URL}/api/auth/impersonate/${targetUser.user_id}`, {}, { withCredentials: true });
      await checkAuth();
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.impersonateFailed'));
    }
  };

  const getAvailableProjects = () => {
    const assignedIds = userProjects.map(p => p.project_id);
    return projects.filter(p => !assignedIds.includes(p.project_id));
  };

  const pendingCount = users.filter(u => u.role === 'pending').length;
  const startItem = totalItems === 0 ? 0 : (page - 1) * perPage + 1;
  const endItem = Math.min(page * perPage, totalItems);

  if (loading && users.length === 0) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-100 tracking-tight" style={{ fontFamily: "'Outfit', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{t('header.title')}</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-2" style={{ fontFamily: "'Plus Jakarta Sans', -apple-system, 'Segoe UI', Roboto, sans-serif" }}>{t('header.subtitle')}</p>
        </div>
        {!isReadOnly && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openAddDialog} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" />{t('header.addUser')}</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? t('dialog.editTitle') : t('dialog.addTitle')}</DialogTitle>
              <DialogDescription>
                {editingUser ? t('dialog.editDescription') : t('dialog.addDescription')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">{t('common:fields.email')}</Label>
                <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="user@example.com" required disabled={!!editingUser} data-testid="user-email-input" />
              </div>
              <div>
                <Label htmlFor="name">{t('dialog.fullName')}</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('dialog.fullNamePlaceholder')} required data-testid="user-name-input" />
              </div>
              <div>
                <Label htmlFor="role">{t('dialog.role')}</Label>
                <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{t(`common:roles.${r}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {t('dialog.roleHint')}
                </p>
              </div>
              {formData.role === 'employee' && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                  <div>
                    <Label htmlFor="can-view-finance" className="text-sm font-medium">{t('dialog.canViewFinance')}</Label>
                    <p className="text-xs text-slate-500 mt-0.5">{t('dialog.canViewFinanceHint')}</p>
                  </div>
                  <Switch
                    id="can-view-finance"
                    checked={formData.can_view_finance}
                    onCheckedChange={(val) => setFormData({ ...formData, can_view_finance: val })}
                    data-testid="can-view-finance-toggle"
                  />
                </div>
              )}
              {(formData.role === 'employee' || formData.role === 'accountant') && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                  <div>
                    <Label htmlFor="can-view-analytics" className="text-sm font-medium">{t('dialog.canViewAnalytics')}</Label>
                    <p className="text-xs text-slate-500 mt-0.5">{t('dialog.canViewAnalyticsHint')}</p>
                  </div>
                  <Switch
                    id="can-view-analytics"
                    checked={formData.can_view_analytics}
                    onCheckedChange={(val) => setFormData({ ...formData, can_view_analytics: val })}
                    data-testid="can-view-analytics-toggle"
                  />
                </div>
              )}
              <div className="flex gap-2">
                <Button type="submit" data-testid="submit-user-btn">{editingUser ? t('common:actions.update') : t('common:actions.create')}</Button>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t('common:actions.cancel')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-end bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">{t('common:actions.search')}</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('filters.searchPlaceholder')}
                className="pl-9 w-[220px]"
                data-testid="user-search"
              />
            </div>
          </div>
          <div className="flex items-end">
            <Button type="submit" size="sm" variant="secondary" className="h-10">
              {t('common:actions.search')}
            </Button>
          </div>
        </form>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">{t('filters.role')}</Label>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="w-[150px]" data-testid="user-role-filter">
              <SelectValue placeholder={t('filters.allRoles')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allRoles')}</SelectItem>
              {ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{t(`common:roles.${r}`)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-500 mb-1 block">{t('common:fields.status')}</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="user-status-filter">
              <SelectValue placeholder={t('filters.allStatuses')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('filters.allStatuses')}</SelectItem>
              <SelectItem value="active">{t('common:status.active')}</SelectItem>
              <SelectItem value="inactive">{t('common:status.inactive')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-10 text-slate-500">
            <X className="h-4 w-4 mr-1" />
            {t('filters.clear')}
          </Button>
        )}
      </div>

      {/* Pending Users Alert */}
      {pendingCount > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-900 dark:text-amber-100">
                {t('pending.title')}
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                {t('pending.message', { count: pendingCount })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="flex items-center gap-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <UsersIcon className="h-5 w-5 text-emerald-600" />
        <span className="text-sm text-slate-600 dark:text-slate-400">{t('summary.found', { count: totalItems })}</span>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.user')}</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.email')}</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.role')}</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.status')}</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('table.projects')}</th>
              <th className="px-6 py-3 text-left text-xs uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('common:fields.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                  {t('table.empty')}
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.user_id} className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors duration-150 ${user.role === 'pending' ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''}`} data-testid={`user-row-${user.user_id}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <img src={user.picture || 'https://via.placeholder.com/40'} alt={user.name} className="h-8 w-8 rounded-full" />
                      <span className="text-sm text-slate-700 dark:text-slate-300">{user.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700 dark:text-slate-300">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium capitalize ${
                      user.role === 'super_admin' || user.role === 'admin'
                        ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                        : user.role === 'pending'
                        ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                        : user.role === 'employee'
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                        : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                    }`}>
                      {t(`common:roles.${user.role}`, { defaultValue: user.role.replace('_', ' ') })}
                      {user.role === 'employee' && user.can_view_finance && (
                        <span className="ml-1 text-[10px] opacity-70" title={t('table.financeBadge')}>💰</span>
                      )}
                      {user.can_view_analytics && (
                        <span className="ml-1 text-[10px] opacity-70" title={t('table.analyticsBadge')}>📊</span>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Switch checked={user.is_active} onCheckedChange={() => !isReadOnly && toggleUserStatus(user)} disabled={isReadOnly} data-testid={`toggle-user-${user.user_id}`} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.role === 'super_admin' || user.role === 'admin' ? (
                      <span className="text-xs text-slate-500">{t('table.allProjects')}</span>
                    ) : isReadOnly ? (
                      <span className="text-xs text-slate-500">—</span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => openProjectDialog(user)} data-testid={`manage-projects-${user.user_id}`}>
                        <FolderPlus className="h-4 w-4 mr-1" />
                        {t('table.manage')}
                      </Button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      {canImpersonate && user.user_id !== currentUser?.user_id && user.role !== 'super_admin' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleImpersonate(user)}
                          title={t('table.impersonate', { name: user.name || user.email })}
                          data-testid={`impersonate-user-${user.user_id}`}
                        >
                          <UserCog className="h-4 w-4 text-amber-600" />
                        </Button>
                      )}
                      {!isReadOnly && (
                        <>
                          {user.role === 'pending' && (
                            <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => quickApproveUser(user)} data-testid={`approve-user-${user.user_id}`}>
                              <Check className="h-4 w-4 mr-1" />
                              {t('table.approve')}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.user_id}`}>
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(user.user_id)} data-testid={`delete-user-${user.user_id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination Footer */}
        {totalItems > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              {t('pagination.showing', { start: startItem, end: endItem, total: totalItems })}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">{t('pagination.rows')}</span>
                <Select value={perPage.toString()} onValueChange={(val) => setPerPage(parseInt(val))}>
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPage(pageNum)}
                      className="w-8 h-8 p-0"
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Project Assignment Dialog */}
      <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('projectsDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('projectsDialog.description', { name: selectedUser?.name })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Current Assignments */}
            <div>
              <Label className="text-sm font-medium">{t('projectsDialog.assigned', { count: userProjects.length })}</Label>
              {userProjects.length === 0 ? (
                <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    {t('projectsDialog.noneAssigned')}
                  </p>
                </div>
              ) : (
                <div className="mt-2 space-y-2">
                  {userProjects.map(project => (
                    <div key={project.project_id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <span className="text-sm text-slate-700 dark:text-slate-300">{project.name}</span>
                      <Button size="sm" variant="ghost" onClick={() => unassignProject(project.project_id)} data-testid={`unassign-project-${project.project_id}`}>
                        <X className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Projects */}
            <div>
              <Label className="text-sm font-medium">{t('projectsDialog.available')}</Label>
              {getAvailableProjects().length === 0 ? (
                <p className="mt-2 text-sm text-slate-500">{t('projectsDialog.noneAvailable')}</p>
              ) : (
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
                  {getAvailableProjects().map(project => (
                    <div key={project.project_id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-900 rounded-lg">
                      <span className="text-sm text-slate-700 dark:text-slate-300">{project.name}</span>
                      <Button size="sm" variant="outline" onClick={() => assignProject(project.project_id)} data-testid={`assign-project-${project.project_id}`}>
                        <Plus className="h-4 w-4 mr-1" />
                        {t('projectsDialog.assign')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button variant="outline" onClick={() => setProjectDialogOpen(false)}>{t('projectsDialog.done')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Users;
